// src/slm/jsonRepair.js
// Local models routinely get cut off mid-document by max_tokens (an
// unclosed string, array, or object — the exact class of error reported
// live: "Expected ',' or ']' after array element", the response ending
// mid-sentence inside a string value). `jsonrepair` patches exactly that
// class of syntax error (unclosed brackets/quotes, trailing commas,
// unquoted keys, ...). It does NOT validate or coerce SHAPE (wrong keys,
// wrong types, a card label that doesn't exist) — that's a different
// problem, already handled downstream by each caller's own
// whitelisting/coercion (finalizeMentorParsed, parseScenarioProposal's
// enum checks, ...) once the object successfully exists. Repair only
// answers "is this even parseable", not "is this the right shape".
import { jsonrepair } from "jsonrepair";

// Extracts the {...} JSON object substring from `raw` and parses it. Three
// attempts, cheapest/most-common first:
//
// 1. Straight JSON.parse of `{ ... last "}" in the text }` — the common
//    case: a complete, valid JSON object, possibly followed by trailing
//    prose noise the model added despite instructions not to.
// 2. jsonrepair on the FULL remainder from the first "{" to the true end
//    of `raw` — the truncation case (this call's actual reason to exist).
//    Deliberately NOT clipped to the last "}": when the response is cut
//    off by max_tokens partway through an outer object, an inner object
//    or array earlier in the document (e.g. one "evidences" entry) can
//    still have its own closing "}" — that's the LAST "}" in the text even
//    though it isn't the document's real end, and clipping there would
//    silently throw away everything genuine that came after it (a whole
//    "brief" section, in the reported case) before repair even runs.
// 3. jsonrepair on the clipped candidate from step 1 — covers the reverse
//    edge case, valid-JSON-plus-trailing-garbage that itself has a minor
//    fixable issue: jsonrepair fixes syntax, it does not strip unrelated
//    text after a document that already closed correctly (attempt 2 would
//    fail on that trailing text), so clipping first is needed here.
//
// Returns null if all three fail — a genuinely different kind of response
// (not JSON at all), not something repair can be expected to fix.
export function extractAndParseJSON(raw) {
  const text = raw || "";
  const start = text.indexOf("{");
  if (start === -1) return null;

  const lastBrace = text.lastIndexOf("}");
  const clipped = lastBrace > start ? text.slice(start, lastBrace + 1) : null;
  const toEnd = text.slice(start);

  if (clipped) {
    try {
      return JSON.parse(clipped);
    } catch (e) {
      // fall through
    }
  }
  try {
    return JSON.parse(jsonrepair(toEnd));
  } catch (e) {
    // fall through
  }
  if (clipped) {
    try {
      return JSON.parse(jsonrepair(clipped));
    } catch (e) {
      // give up below
    }
  }
  return null;
}
